/** @jsx jsx */
import React, { useEffect } from 'react';
import { jsx } from '@compiled/react';
import { HeadingItem, Section } from '@atlaskit/menu';
import {
	SOFTWARE_PROJECT,
	CORE_PROJECT,
	SERVICE_DESK_PROJECT,
} from '@atlassian/jira-common-constants/src/project-types.tsx';
import { useExperienceSuccess } from '@atlassian/jira-experience-tracker/src/ui/experience-success/index.tsx';
import { useIntl } from '@atlassian/jira-intl';
import { testIdConcat } from '@atlassian/jira-navigation-apps-common/src/utils/test-id.tsx';
import { useProjectContext } from '@atlassian/jira-providers-project-context/src/index.tsx';
import { userBoardRoute } from '@atlassian/jira-router-routes-user-board-routes/src/userBoardRoute.tsx';
import { BUSINESS } from '@atlassian/jira-servicedesk-onboarding-checklist/src/services/get-onboarding-type/constants.tsx';
import { useJsmOnboardingType } from '@atlassian/jira-servicedesk-onboarding-checklist/src/services/get-onboarding-type/main.tsx';
import { OpenMenuItem as JswQuickstartOpenMenuItem } from '@atlassian/jira-software-onboarding-quickstart/src/ui/async.tsx';
import { EXPERIENCE_NAVIGATION_TOP_MENU } from '../../../common/constants.tsx';
import { MenuItem } from '../../../common/ui/menu/item/index.tsx';
import { MenuList, MenuListItem } from '../../../common/ui/menu/list/index.tsx';
import { ExtraItemsNav4 } from './extra-items/index.tsx';
import messages from './messages.tsx';
import type { MenuProps } from './types.tsx';

const StandardToPremiumPubTouchpoint = importCond<
	typeof import('@atlassian/jira-edition-awareness-for-standard-to-premium/src/ui/standard-to-premium-pub-touchpoint/index.tsx'),
	typeof import('@atlassian/jira-edition-awareness-for-standard-to-premium/src/ui/standard-to-premium-pub-touchpoint/index-old.tsx')
>(
	'edition_awareness_upsell_jira_ab_fg',
	'@atlassian/jira-edition-awareness-for-standard-to-premium/src/ui/standard-to-premium-pub-touchpoint/index.tsx',
	'@atlassian/jira-edition-awareness-for-standard-to-premium/src/ui/standard-to-premium-pub-touchpoint/index-old.tsx',
);

export const MenuNav4 = ({ testIdPrefix, userBoardId, userBoardOwnerAccountId }: MenuProps) => {
	const { formatMessage } = useIntl();
	const onSuccess = useExperienceSuccess({
		experience: EXPERIENCE_NAVIGATION_TOP_MENU,
	});

	useEffect(() => {
		onSuccess();
	}, [onSuccess]);

	const testId = testIdConcat(testIdPrefix, 'menu-popup');
	const testIdContent = testIdConcat(testId, 'content');
	const { data: projectContextData } = useProjectContext();
	const [{ data: jsmOnboardingData }, { fetchOnboardingType }] = useJsmOnboardingType();

	useEffect(() => {
		fetchOnboardingType(projectContextData?.projectId, projectContextData?.projectType);
	}, [fetchOnboardingType, projectContextData?.projectId, projectContextData?.projectType]);

	const showInJwmProjects = projectContextData?.projectType === CORE_PROJECT;
	const showInSoftwareProjects = projectContextData?.projectType === SOFTWARE_PROJECT;
	const showInJsmProjects =
		projectContextData?.projectType === SERVICE_DESK_PROJECT &&
		jsmOnboardingData?.onboardingType === BUSINESS;

	let yourBoardsRoute;
	let yourBoardsParams;
	let yourBoardsQuery;
	if (userBoardId != null) {
		yourBoardsRoute = userBoardRoute;
		yourBoardsParams = {
			accountId: userBoardOwnerAccountId,
			boardId: String(userBoardId),
		};
	}

	const boardOffset = userBoardId == null ? -1 : 0;
	const showQuickstartOpenMenuItem =
		showInJwmProjects || showInSoftwareProjects || showInJsmProjects;

	return (
		<MenuList>
			{showQuickstartOpenMenuItem && <JswQuickstartOpenMenuItem MenuItem={MenuItem} />}
			{userBoardId !== null && (
				<Section hasSeparator testId={testIdContent}>
					<span data-testid={testIdConcat(testIdContent, 'jira')}>
						<HeadingItem testId={testIdConcat(testIdContent, 'jira', 'heading')}>
							{formatMessage(messages.jira)}
						</HeadingItem>
						<MenuListItem ariaLabel={formatMessage(messages.jira)}>
							<MenuItem
								analytics={{
									actionSubjectId: 'profileNavigationMenuItem',
									menuItemId: 'your-boards',
									menuItemType: 'jira',
									position: 1,
								}}
								href={`/jira/people/${String(userBoardOwnerAccountId)}/boards/${userBoardId}`}
								params={yourBoardsParams}
								query={yourBoardsQuery}
								testId={testIdConcat(testIdContent, 'jira', 'item-your-boards')}
								to={yourBoardsRoute}
							>
								{formatMessage(messages.yourBoards)}
							</MenuItem>
						</MenuListItem>
					</span>
				</Section>
			)}
			<StandardToPremiumPubTouchpoint />
			<ExtraItemsNav4
				startPosition={5 + boardOffset}
				testIdPrefix={testIdContent}
				itemAriaLabel={formatMessage(messages.jira)}
			/>
		</MenuList>
	);
};
